import React from 'react';
import { motion } from 'framer-motion';
import { useInvestigation } from '../../../contexts/InvestigationContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { 
  FiCheckCircle, 
  FiClock, 
  FiPlay, 
  FiMail,
  FiFileText,
  FiAlertTriangle,
  FiSettings
} = FiIcons;

const AutomaticProceduresPanel = () => {
  // Sikker destrukturering av context
  const context = useInvestigation();
  
  // Fallback til tomme verdier hvis context mangler data
  const procedures = context?.procedures || [];
  const getActiveProcedures = context?.getActiveProcedures;
  
  // Sikker kjøring av getActiveProcedures
  const activeProcedures = typeof getActiveProcedures === 'function' 
    ? getActiveProcedures() 
    : [];

  // Sikker filtrering og sortering
  const recentProcedures = Array.isArray(procedures) ? procedures
    .filter(p => p.status === 'completed')
    .sort((a, b) => {
      const dateA = new Date(a.completedAt || a.createdAt || 0);
      const dateB = new Date(b.completedAt || b.createdAt || 0);
      return dateB.getTime() - dateA.getTime();
    })
    .slice(0, 5) : [];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'in_progress': return 'text-blue-600 bg-blue-100';
      case 'scheduled': return 'text-yellow-600 bg-yellow-100';
      case 'failed': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return FiCheckCircle;
      case 'in_progress': return FiPlay;
      case 'scheduled': return FiClock;
      default: return FiSettings;
    }
  };

  const getProcedureIcon = (type) => {
    switch (type) {
      case 'initial_registration': return FiFileText;
      case 'notification_dispatch': return FiMail;
      case 'investigation_trigger': return FiAlertTriangle;
      default: return FiSettings;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleString('nb-NO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  if (!Array.isArray(procedures) || procedures.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-100">
        <SafeIcon icon={FiSettings} className="text-gray-400 text-4xl mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Ingen prosedyrer</h3>
        <p className="text-gray-500">Ingen automatiske prosedyrer er kjørt ennå.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Active Procedures */}
      {activeProcedures.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
            <SafeIcon icon={FiPlay} className="text-blue-600" />
            <span>Aktive prosedyrer</span>
          </h3>
          
          <div className="space-y-4">
            {activeProcedures.map((procedure) => (
              <motion.div
                key={procedure.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="border border-gray-200 rounded-lg p-4 bg-blue-50"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <SafeIcon 
                      icon={getProcedureIcon(procedure.type)} 
                      className="text-blue-600 text-xl" 
                    />
                    <div>
                      <h4 className="font-medium text-gray-900">{procedure.title}</h4>
                      <p className="text-sm text-gray-600">Varsel: {procedure.reportId}</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(procedure.status)}`}>
                    {procedure.status === 'in_progress' ? 'Pågår' : 'Planlagt'}
                  </span>
                </div>
                
                {procedure.actions && (
                  <div className="space-y-2">
                    {procedure.actions.map((action, index) => (
                      <div key={index} className="flex items-center space-x-2 text-sm text-gray-700">
                        <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                        <span>{action}</span>
                      </div>
                    ))}
                  </div>
                )}
                
                {procedure.scheduledFor && (
                  <div className="mt-3 text-sm text-gray-600">
                    <SafeIcon icon={FiClock} className="inline mr-1" />
                    Planlagt: {formatDateTime(procedure.scheduledFor)}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Recent Completed Procedures */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
          <SafeIcon icon={FiCheckCircle} className="text-green-600" />
          <span>Nylig utførte prosedyrer</span>
        </h3>
        
        <div className="space-y-3">
          {recentProcedures.map((procedure) => (
            <div
              key={procedure.id}
              className="border border-gray-200 rounded-lg p-4 bg-green-50"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-3">
                  <SafeIcon 
                    icon={getProcedureIcon(procedure.type)} 
                    className="text-green-600" 
                  />
                  <div>
                    <h4 className="font-medium text-gray-900 text-sm">{procedure.title}</h4>
                    <p className="text-xs text-gray-600">Varsel: {procedure.reportId}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Fullført
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatDateTime(procedure.completedAt)}
                  </p>
                </div>
              </div>
              
              {procedure.actions && (
                <div className="space-y-1">
                  {procedure.actions.slice(0, 2).map((action, index) => (
                    <div key={index} className="flex items-center space-x-2 text-xs text-gray-700">
                      <SafeIcon icon={FiCheckCircle} className="text-green-500 text-xs" />
                      <span>{action}</span>
                    </div>
                  ))}
                  {procedure.actions.length > 2 && (
                    <p className="text-xs text-gray-500 ml-4">
                      +{procedure.actions.length - 2} flere handlinger
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        
        {recentProcedures.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <SafeIcon icon={FiSettings} className="text-4xl mx-auto mb-2" />
            <p>Ingen nylige prosedyrer funnet</p>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AutomaticProceduresPanel;